import React, { useState } from 'react';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import { get } from 'lodash'; // Import Sun Editor's CSS File
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { MenuItem, Select } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import PropTypes from 'prop-types';
import { fieldHasOptions } from '../../helpers';
import stripHtmlTags from '../../../../../../../helpers/stripHtmlTags';

const OptionsEditor = ({ options, onChange }) => {
  const [current, setCurrent] = useState(options.length > 0 ? options[0].id : null);

  if (!current) {
    return 'No options available';
  }

  const handleOptionSelection = event => {
    setCurrent(event.target.value);
  };

  const currentOption = options.find(opt => opt.id === current);
  const contextHelp = get(currentOption, 'context_help');

  const handleEditorChange = content => {
    const updatedOptions = options.map(opt =>
      opt.id === current
        ? { ...opt, context_help: content ? { type: 'text', content } : null }
        : opt
    );
    onChange({ props: { options: updatedOptions } });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: '16px' }}>
      <FormControl fullWidth>
        <InputLabel id="context-help-option-selection">Select Option</InputLabel>
        <Select
          variant="outlined"
          value={currentOption.id}
          labelId="context-help-option-selection"
          onChange={handleOptionSelection}
          fullWidth
        >
          {options.map(option => {
            return (
              <MenuItem value={option.id} key={option.id}>
                {option.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Editor key={current} content={contextHelp?.content} onChange={handleEditorChange} />
    </Box>
  );
};

OptionsEditor.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      context_help: PropTypes.shape({
        type: PropTypes.string,
        content: PropTypes.string
      })
    })
  ),
  onChange: PropTypes.func.isRequired
};

OptionsEditor.defaultProps = {
  options: []
};

const Editor = ({ content, onChange }) => {
  return (
    <SunEditor
      onChange={updatedContent => {
        if (updatedContent.length < 20 && !stripHtmlTags(updatedContent).trim()) {
          onChange(null);
        }
        onChange(updatedContent);
      }}
      setContents={content}
      setOptions={{
        height: 'auto',
        minHeight: '150px',
        buttonList: [
          ['undo', 'redo'],
          ['fontSize', 'formatBlock'],
          ['bold', 'underline', 'italic', 'strike', 'fontColor', 'hiliteColor'],
          ['outdent', 'indent'],
          ['align', 'horizontalRule', 'list', 'lineHeight'],
          ['link', 'image', 'video'],
          ['codeView']
        ]
      }}
    />
  );
};

Editor.propTypes = {
  content: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

Editor.defaultProps = {
  content: ''
};

const Main = ({ elementType, form, setForm }) => {
  if (fieldHasOptions(elementType)) {
    return (
      <OptionsEditor
        options={get(form, 'props.options', [])}
        onChange={value =>
          setForm(prev => {
            return { ...prev, props: { ...prev.props, ...value.props } };
          })
        }
      />
    );
  }

  // eslint-disable-next-line react/prop-types
  const { context_help: contextHelp } = form;
  // eslint-disable-next-line react/prop-types
  const content = contextHelp?.content;

  return (
    <Editor
      onChange={value => {
        setForm(prev => {
          return {
            ...prev,
            context_help: value ? { type: 'text', content: value } : null
          };
        });
      }}
      content={content}
    />
  );
};

Main.propTypes = {
  elementType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired
};

export default Main;
